import { useState, useEffect, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'

const API = import.meta.env.VITE_API_GATEWAY_URL

async function authHeaders() {
    const session = await fetchAuthSession()
    return { Authorization: `Bearer ${session.tokens.idToken.toString()}` }
}

function EmpresasManager() {
    const [empresas, setEmpresas] = useState([])
    const [loading, setLoading] = useState(true)
    const [showForm, setShowForm] = useState(false)
    const [editEmpresa, setEditEmpresa] = useState(null)
    const [form, setForm] = useState({ id: '', nombre: '', plan: 'basico' })
    const [saving, setSaving] = useState(false)
    const [msg, setMsg] = useState(null)

    const loadEmpresas = useCallback(async () => {
        setLoading(true)
        try {
            const headers = await authHeaders()
            const res = await axios.get(`${API}/admin/empresas`, { headers })
            setEmpresas(res.data.empresas || [])
        } catch (err) {
            setMsg({ type: 'error', text: 'Error cargando empresas: ' + err.message })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { loadEmpresas() }, [loadEmpresas])

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSaving(true)
        setMsg(null)
        try {
            const headers = await authHeaders()
            if (editEmpresa) {
                await axios.put(`${API}/admin/empresas/${editEmpresa.id}`, form, { headers })
                setMsg({ type: 'success', text: 'Empresa actualizada correctamente' })
            } else {
                await axios.post(`${API}/admin/empresas`, form, { headers })
                setMsg({ type: 'success', text: `Empresa "${form.nombre}" creada exitosamente` })
            }
            setShowForm(false)
            setEditEmpresa(null)
            setForm({ id: '', nombre: '', plan: 'basico' })
            await loadEmpresas()
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || err.message })
        } finally {
            setSaving(false)
        }
    }

    const handleToggleActiva = async (empresa) => {
        try {
            const headers = await authHeaders()
            await axios.put(`${API}/admin/empresas/${empresa.id}`, { activa: !empresa.activa }, { headers })
            await loadEmpresas()
        } catch (err) {
            setMsg({ type: 'error', text: 'Error actualizando empresa' })
        }
    }

    const handleEdit = (empresa) => {
        setEditEmpresa(empresa)
        setForm({ id: empresa.id, nombre: empresa.nombre, plan: empresa.plan || 'basico' })
        setShowForm(true)
    }

    return (
        <div className="admin-section">
            <div className="admin-section-header">
                <div>
                    <h2>🏢 Empresas</h2>
                    <p>Gestiona las empresas clientes de Ingconia</p>
                </div>
                <button className="btn-primary" onClick={() => { setShowForm(true); setEditEmpresa(null); setForm({ id: '', nombre: '', plan: 'basico' }) }}>
                    + Nueva Empresa
                </button>
            </div>

            {msg && (
                <div className={`admin-alert admin-alert--${msg.type}`}>
                    {msg.text}
                    <button onClick={() => setMsg(null)}>✕</button>
                </div>
            )}

            {/* Formulario */}
            {showForm && (
                <div className="admin-form-card">
                    <h3>{editEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>ID de empresa *</label>
                                <input
                                    type="text"
                                    value={form.id}
                                    onChange={e => setForm(f => ({ ...f, id: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                                    placeholder="ej: meq, xilmedical, sr"
                                    required
                                    disabled={!!editEmpresa}
                                />
                                {!editEmpresa && <span className="form-hint">Debe ser único, sin espacios, en minúsculas</span>}
                            </div>
                            <div className="form-group">
                                <label>Nombre de la empresa *</label>
                                <input
                                    type="text"
                                    value={form.nombre}
                                    onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                                    placeholder="ej: MEQ S.A.S."
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Plan</label>
                                <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
                                    <option value="basico">Básico</option>
                                    <option value="profesional">Profesional</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-actions">
                            <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
                            <button type="submit" className="btn-primary" disabled={saving}>
                                {saving ? 'Guardando...' : (editEmpresa ? 'Guardar Cambios' : 'Crear Empresa')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Lista */}
            {loading ? (
                <div className="admin-loading-inline"><div className="admin-spinner-sm"></div> Cargando...</div>
            ) : empresas.length === 0 ? (
                <div className="admin-empty">
                    <span>🏢</span>
                    <p>No hay empresas registradas aún.</p>
                    <p>Crea la primera empresa con el botón de arriba.</p>
                </div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nombre</th>
                                <th>Plan</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {empresas.map(emp => (
                                <tr key={emp.id}>
                                    <td><code className="code-badge">{emp.id}</code></td>
                                    <td><strong>{emp.nombre}</strong></td>
                                    <td><span className={`plan-badge plan-badge--${emp.plan || 'basico'}`}>{emp.plan || 'básico'}</span></td>
                                    <td>
                                        <span className={`status-badge ${emp.activa ? 'status-active' : 'status-inactive'}`}>
                                            {emp.activa ? '● Activa' : '○ Inactiva'}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <button className="btn-sm btn-ghost" onClick={() => handleEdit(emp)}>Editar</button>
                                        <button className="btn-sm btn-ghost" onClick={() => handleToggleActiva(emp)}>
                                            {emp.activa ? 'Desactivar' : 'Activar'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

export default EmpresasManager
